import { useMemo } from 'react'
import {
  useGetAllImportedGoods,
  useGetGoodsReceivedReports
} from '@/features/cbam-reporting/hooks/importedGoods'
import {
  type ImportedGoodExtended,
  type NoneLabels,
  useImportedGoodsFilters
} from '@/features/cbam-reporting/hooks/useImportedGoodsFilters'
import { useGetAllSupplierEmissionsReportsForOrganization } from '@/features/suppliers/hooks/supplierEmissionsReport'
import type { Partnership } from '@/features/suppliers/hooks/partnerships'
import type { Installation } from '@/features/suppliers/hooks/installations'
import type { Importer } from '@/features/importers/hooks/importers'
import type { SupplierEmissionsReport } from '@/features/suppliers/hooks/supplierEmissionsReport'

export type CbamReportImportedGoodsFiltersConfig = {
  partnerships: Partnership[]
  installations: Installation[]
  importers: Importer[]
  isRepresentative: boolean
  reportingPeriodYear: number
  getCountryLabel: (countryCode: string) => string
  getAggregatedGoodsCategoryLabel: (category: string) => string
  getCnCodeLabel: (cnCode: string) => string
  noneLabels: NoneLabels
}

/**
 * Loads imported goods for a CBAM report and supplier emissions reports, enriches rows,
 * and owns `useImportedGoodsFilters` state (full list for options, `filteredGoods` for the table).
 */
export function useCbamReportImportedGoods(
  organizationId: string,
  cbamReportId: string,
  filtersConfig: CbamReportImportedGoodsFiltersConfig
) {
  const importedGoodsQuery = useGetAllImportedGoods(organizationId)
  const supplierEmissionsReportsQuery =
    useGetAllSupplierEmissionsReportsForOrganization(organizationId)
  const goodsReceivedReportsQuery = useGetGoodsReceivedReports(organizationId, cbamReportId)

  const importedGoodsForCbamReport = useMemo(
    () =>
      (importedGoodsQuery.data ?? []).filter(
        good => good.deletedAt === null && good.cbamReportId === cbamReportId
      ),
    [importedGoodsQuery.data, cbamReportId]
  )

  const supplierEmissionsReports = useMemo(
    (): SupplierEmissionsReport[] => supplierEmissionsReportsQuery.data ?? [],
    [supplierEmissionsReportsQuery.data]
  )

  const goodsReceivedReportById = useMemo(() => {
    const list = goodsReceivedReportsQuery.data ?? []
    return new Map(list.map(grr => [grr.id, grr]))
  }, [goodsReceivedReportsQuery.data])

  const importedGoodsExtended = useMemo<ImportedGoodExtended[]>(
    () =>
      importedGoodsForCbamReport.map(good => {
        const grr = good.goodsReceivedReportId
          ? goodsReceivedReportById.get(good.goodsReceivedReportId)
          : undefined
        const fileName = grr?.fileName ?? null
        return {
          ...good,
          emissionsReport:
            supplierEmissionsReports.find(report => report.id === good.supplierEmissionsReportId) ??
            null,
          goodsReceivedReportFileName: fileName
        }
      }),
    [importedGoodsForCbamReport, supplierEmissionsReports, goodsReceivedReportById]
  )

  const importedGoodsFilters = useImportedGoodsFilters(
    importedGoodsExtended,
    filtersConfig.partnerships,
    filtersConfig.installations,
    supplierEmissionsReports,
    filtersConfig.importers,
    filtersConfig.isRepresentative,
    filtersConfig.reportingPeriodYear,
    filtersConfig.getCountryLabel,
    filtersConfig.getAggregatedGoodsCategoryLabel,
    filtersConfig.getCnCodeLabel,
    filtersConfig.noneLabels
  )

  const isLoadingImportedGoods =
    importedGoodsQuery.isLoading ||
    supplierEmissionsReportsQuery.isLoading ||
    goodsReceivedReportsQuery.isLoading

  return {
    cbamReportId,
    importedGoodsExtended,
    supplierEmissionsReports,
    isLoadingImportedGoods,
    ...importedGoodsFilters
  }
}
